import React, {useCallback, useEffect, useState} from 'react';
import {observer} from 'mobx-react';
import I18n from 'app/i18n';
import {checkingPages, contentOnly, isGuest} from 'app/utils';
import {goToLinkSimpleBets} from 'app/utils/app_links';
import logEvent from 'app/amplitude/log_event';
import {setStreamParam, setStreamsTimeout} from 'app/components/streams/stream_functions';
import Stream from 'app/components/streams/stream';
import {streamStore} from 'app/store/global';
import {openAddStreamPopup} from 'app/components/streams';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import {openAuth} from 'app/components/auth/auth';
import AsideItem from 'site_version/com/AsideItem';
import doubleArrDownSvg from 'images/svg_icons/double-arr-down.svg';
import type StreamInterface from 'app/interfaces/StreamInterface';

const CLOSED_STREAM_LIMIT = 6;
const ASIDE_ICON_SIZE = '24px';
const ADD_STREAM_ICON_SIZE = 32;
const USER_CREATED_AUTHOR = 'by_user_created';

const StreamsAside = observer((): React.ReactElement | null => {
    const [opened, setOpened] = useState(false);

    useEffect(() => {
        setStreamsTimeout();
    }, []);

    const handleOpenStream = useCallback((stream: StreamInterface): void => {
        logEvent('STREAM_OPENED');

        const opensInNewTab = Boolean(stream && (stream.target_blank || stream.stream_url));

        if (!opensInNewTab && !checkingPages.isPlayPage()) {
            goToLinkSimpleBets();
        } else {
            setStreamParam(stream);
        }

        streamStore.chooseStream(stream);
    }, []);

    const handleStreamCreationPopup = useCallback((event: React.MouseEvent<HTMLAnchorElement>): void => {
        event.preventDefault();

        if (isGuest()) {
            openAuth();
        } else {
            openAddStreamPopup();
        }
    }, []);

    const handleToggleOpened = useCallback((): void => {
        setOpened(isOpened => !isOpened);
    }, []);

    const renderStream = useCallback((stream: StreamInterface): React.ReactElement => <Stream
        stream={stream}
        preview="aside"
        key={stream.id}
        iconSize={ASIDE_ICON_SIZE}
        imageLastUpdate={streamStore.imageLastUpdate}
        userStream={stream.author_name === USER_CREATED_AUTHOR}
        onClick={handleOpenStream}
    />, [handleOpenStream]);

    if (contentOnly()) {
        return null;
    }

    const allStreams = [...streamStore.streams, ...streamStore.userStreams] as StreamInterface[];
    const visibleStreams = opened ? allStreams : allStreams.slice(0, CLOSED_STREAM_LIMIT);
    const hiddenCount = allStreams.length - CLOSED_STREAM_LIMIT;
    const expandLabel = opened ? I18n.t('stream.less') : I18n.t('stream.more');
    const moreClassName = `stream-aside__more${opened ? ' stream-aside__more--opened' : ''}`;

    const header = <div className="aside-item-heading__title">
        <h3>{I18n.t('stream_title')}</h3>
    </div>;

    return <AsideItem
        header={header}
        itemClassName="stream-aside"
        name="streams"
    >
        <div className="stream-aside__streams">
            {visibleStreams.map(renderStream)}
            {(opened || hiddenCount < 0) && <a
                href="#"
                onClick={handleStreamCreationPopup}
                className="media-preview media-preview--aside media-preview--add-stream"
            >
                <span title={I18n.t('add_your_own')}>
                    <SpriteIcon
                        name="#svg-stream-add"
                        size={{height: ADD_STREAM_ICON_SIZE, width: ADD_STREAM_ICON_SIZE}}
                    />
                </span>
                <span className="media-preview__name">
                    <span className="media-preview__name-text text-center">{I18n.t('add_your_own')}</span>
                </span>
            </a>}
        </div>
        {hiddenCount >= 0 && <button
            type="button"
            className={moreClassName}
            onClick={handleToggleOpened}
        >
            {expandLabel}
            <img
                src={doubleArrDownSvg}
                alt={expandLabel}
                className="stream-aside__more-icon"
            />
        </button>}
    </AsideItem>;
});

export default StreamsAside;
